import AppModal from "@components/modals/AppModal";
import ThinkingAnimation from "@utils/ui/ThinkingAnimation";
import "@assets/css/mode_tool/translatePointsModal.css";

function TranslatePointsModal({
  isOpen,
  onClose,
  languages,
  selectedLanguage,
  onSelectLanguage,
  pointScope = "all",
  onSelectPointScope,
  isSinglePoint = false,
  onTranslate,
  isTranslating,
  pointCount,
  error,
  failedPoints = [],
  streamProgress = {},
}) {
  const options = (Array.isArray(languages) ? languages : []).filter(
    (language) => String(language?.iso || "").toLowerCase() !== "en",
  );
  const streamPoints = Array.isArray(streamProgress.points) ? streamProgress.points : [];
  const total = Number(streamProgress.total) || streamPoints.length;
  const completed = Number(streamProgress.completed) || 0;
  const progressPercent = total ? Math.min(100, Math.round((completed / total) * 100)) : 0;
  const counts = streamPoints.reduce((result, item) => {
    const status = String(item?.status || "pending");
    result[status] = (result[status] || 0) + 1;
    return result;
  }, {});

  return (
    <AppModal isOpen={isOpen} onClose={onClose} closeDisabled={isTranslating} title="AI translate points" size="sm" className="mode-route-translate-modal">
      <p>{isSinglePoint
        ? "Translate this point's EN text. Review the result before saving."
        : `Translate the EN text of the ${pointCount} points in this route. Review the result before saving.`}</p>
      {options.length ? (
        <fieldset className="mode-route-translate-languages" disabled={isTranslating}>
          <legend>Target language</legend>
          {options.map((language) => (
            <label key={language.id || language.iso}>
              <input
                type="radio"
                name="route-point-translation-language"
                value={language.iso}
                checked={selectedLanguage === language.iso}
                onChange={() => onSelectLanguage(language.iso)}
              />
              <span>{language.name || String(language.iso).toUpperCase()}</span>
              <small>{String(language.iso).toUpperCase()}</small>
            </label>
          ))}
        </fieldset>
      ) : (
        <p>No languages other than EN are available for this tour.</p>
      )}
      {options.length && !isSinglePoint && !streamProgress.completeReceived ? (
        <fieldset className="mode-route-translate-languages mode-route-ai-point-scope" disabled={isTranslating}>
          <legend>Points to translate</legend>
          {[
            ["all", "All points"],
            ["regular", "Regular points only"],
            ["directions", "Direction points only"],
          ].map(([value, label]) => (
            <label key={value}>
              <input
                type="radio"
                name="route-point-translation-scope"
                value={value}
                checked={pointScope === value}
                onChange={() => onSelectPointScope?.(value)}
              />
              <span>{label}</span>
            </label>
          ))}
        </fieldset>
      ) : null}
      {isTranslating ? <ThinkingAnimation label="Translating points..." /> : null}
      {streamPoints.length ? (
        <section className={`mode-route-ai-stream ${isTranslating ? "is-running" : "is-finished"}`} aria-live="polite">
          <div className="mode-route-ai-stream-heading">
            <strong>{streamProgress.done ? "Translation summary" : "Translating route points"}</strong>
            <span>{completed} / {total} translations</span>
          </div>
          <div className="mode-route-ai-stream-track" role="progressbar" aria-label="Route translation progress" aria-valuemin={0} aria-valuemax={total} aria-valuenow={Math.min(completed, total)}>
            <span style={{ width: `${progressPercent}%` }} />
          </div>
          <div className="mode-route-ai-stream-counts">
            <span className="is-completed">{counts.completed || 0} translated</span>
            <span className="is-error">{counts.error || 0} errors</span>
            <span>{counts.pending || 0} waiting</span>
          </div>
          <ol className="mode-route-ai-stream-list">
            {streamPoints.map((item, index) => {
              const status = String(item?.status || "pending");
              const labels = { completed: "Translated", error: "Error", skipped: "Skipped", pending: "Waiting" };
              const icons = { completed: "✓", error: "!", skipped: "–", pending: "…" };
              return (
                <li className={`is-${status}`} key={item?.id || index}>
                  <span className="mode-route-ai-stream-status" aria-hidden="true">{icons[status] || "•"}</span>
                  <span className="mode-route-ai-stream-point">
                    <strong>#{index + 1} · {item?.label || item?.id || "Route point"}</strong>
                    {item?.translatedName ? <small>{item.translatedName}</small> : null}
                    {item?.message ? <small>{item.message}</small> : null}
                  </span>
                  <span className="mode-route-ai-stream-label">{labels[status] || status}</span>
                </li>
              );
            })}
          </ol>
        </section>
      ) : null}
      {error ? <p className="message-error" role="alert">{error}</p> : null}
      {!streamPoints.length && failedPoints.length ? (
        <section className="mode-route-translate-failures" role="alert">
          <p>These points could not be translated:</p>
          <ul>
            {failedPoints.map((point) => <li key={point.id}>{point.label}</li>)}
          </ul>
        </section>
      ) : null}
      <div className="tour-add-editor-actions">
        <button type="button" className="button-ghost" onClick={onClose} disabled={isTranslating}>
          Close
        </button>
        {!streamProgress.completeReceived ? (
          <button
            type="button"
            className="button-primary"
            onClick={onTranslate}
            disabled={!selectedLanguage || !pointCount || isTranslating}
          >
            {isTranslating ? "Generating..." : "Generate"}
          </button>
        ) : null}
      </div>
    </AppModal>
  );
}

export default TranslatePointsModal;
